import React, { useEffect, useRef } from 'react';
import { Input } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';
import Helpers from '../../../../../../utils/helper';
import {
    PurchasePriceSummary,
    DeferredFixedPaymentRow,
    DeferredVariablePaymentRow,
    DeferredVariablePaymentType,
    CorporateEquityRow,
    CorporateEquityType,
    NO_VALUE,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { TermFormFieldComment } from '../../../../../../slices/marketplace/loi-form/term-form-field-comment';
import { LoiTermFormFieldKeys } from '../../../../../../slices/marketplace/loi-form/loi-term-form-field-keys';
import { PriceSummaryTableRow } from './PriceSummaryTableRow';
import { EmployeeConsiderationPayer } from '../../../../../../slices/marketplace/loi-form/employee-consideration-payer.enum';

export interface TermsFormPriceSummaryBlockProps {
    summary: PurchasePriceSummary | null | undefined;
    total: number;
    onSummaryChange: (patch: Partial<PurchasePriceSummary>) => void;
    readOnly?: boolean;
    errors?: Set<string>;
    employeeConsiderationPayer?: EmployeeConsiderationPayer | null;
    isFsb?: boolean;
    isSfas?: boolean;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    fieldComments?: TermFormFieldComment[];
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
    onAdminCommentChange?: (fieldKey: string, adminComment: string | null) => void;
    isAdmin?: boolean;
}

function formatPercent(val: number, total: number): string | null {
    if (total <= 0 || val <= 0) return null;
    return `${Math.round((val / total) * 1000) / 10}%`;
}

const emptyDfpRow = (amount: number | null): DeferredFixedPaymentRow => ({
    paymentTiming: null,
    amount,
    annualizedInterestAccrualRate: null,
    contingent: NO_VALUE,
    contingencyDependentVariableValue: null,
    contDvMinimumValue: null,
    employmentContingency: NO_VALUE,
    tailPeriodForEmployment: null,
    considerationForEmployees: null,
    fairValue: null,
    fairValueExplanation: null,
});

const emptyDvpRow = (
    valueAtClosing: number | null,
    type?: DeferredVariablePaymentType
): DeferredVariablePaymentRow => ({
    paymentTiming: null,
    type: type ?? null,
    valueAtClosing,
    calculationDependentVariable: null,
    calcDvBaselineValue: null,
    calculationMultiple: null,
    maximumPayment: null,
    employmentContingency: NO_VALUE,
    tailPeriodForEmploymentContingency: null,
    penaltyMultiple: null,
    distributionPeriodicity: null,
    considerationForEmployees: null,
    fairValue: null,
    fairValueExplanation: null,
});

const emptyCorpRow = (
    valueAtClosing: number | null,
    type: CorporateEquityType
): CorporateEquityRow => ({
    type,
    valueAtClosing,
    expectedTimingToRecap: null,
    expectedPayoutAsMultipleOfCurrentValue: null,
    annualDividendRate: null,
    dividendPaymentTiming: null,
    compoundingFrequency: null,
    convertibleIntoCommon: type === CorporateEquityType.Preferred ? false : null,
    conversionRatio: null,
    considerationForEmployees: null,
    fairValue: null,
    fairValueExplanation: null,
});

export const TermsFormPriceSummaryBlock = (
    props: TermsFormPriceSummaryBlockProps
) => {
    const {
        summary,
        total,
        onSummaryChange,
        readOnly = false,
        errors,
        employeeConsiderationPayer,
        isFsb,
        isSfas,
        isBuyerReview,
        isBuyerReviewConfirmed,
        fieldComments,
        onFieldCommentChange,
        onAdminCommentChange,
        isAdmin,
    } = props;

    const getComment = (fieldKey: string) =>
        fieldComments?.find((c) => c.fieldKey === fieldKey)?.buyerComment ?? null;
    const getAdminComment = (fieldKey: string) =>
        fieldComments?.find((c) => c.fieldKey === fieldKey)?.adminComment ?? null;

    const showComments = isBuyerReview || !!isBuyerReviewConfirmed;
    const isAdminCommentEditable = !!isAdmin && !readOnly;
    const showAdminComment = showComments || isAdminCommentEditable;

    const commentInput = (fieldKey?: string) => {
        const comment = fieldKey ? getComment(fieldKey) : null;
        if (!fieldKey) return null;
        return (
            <Input
                type="textarea"
                value={comment ?? ''}
                readOnly={!isBuyerReview}
                onChange={(e) =>
                    onFieldCommentChange?.(
                        fieldKey,
                        e.target.value === '' ? null : e.target.value
                    )
                }
                className="terms-form-comment-input"
                aria-label={`Comment for ${fieldKey}`}
            />
        );
    };

    const adminCommentInput = (fieldKey?: string) => {
        const adminComment = fieldKey ? getAdminComment(fieldKey) : null;
        if (!fieldKey) return null;
        return (
            <Input
                type="textarea"
                value={adminComment ?? ''}
                readOnly={!isAdminCommentEditable}
                onChange={(e) =>
                    onAdminCommentChange?.(
                        fieldKey,
                        e.target.value === '' ? null : e.target.value
                    )
                }
                className="terms-form-comment-input"
                aria-label={`Admin comment for ${fieldKey}`}
            />
        );
    };

    const commentCell = (fieldKey?: string) => {
        if (!showComments) return null;
        const comment = fieldKey ? getComment(fieldKey) : null;
        return (
            <td className="terms-form-col-comment">
                {fieldKey && (isBuyerReview || comment) && (
                    <Input
                        type="textarea"
                        value={comment ?? ''}
                        readOnly={!isBuyerReview}
                        onChange={(e) =>
                            onFieldCommentChange?.(
                                fieldKey,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="terms-form-comment-input"
                        aria-label={`Comment for ${fieldKey}`}
                    />
                )}
            </td>
        );
    };

    const adminCommentCell = (fieldKey?: string) => {
        if (!showAdminComment) return null;
        const adminComment = fieldKey ? getAdminComment(fieldKey) : null;
        return (
            <td className="terms-form-col-comment terms-form-col-admin-comment">
                {fieldKey && (isAdminCommentEditable || adminComment) && (
                    <Input
                        type="textarea"
                        value={adminComment ?? ''}
                        readOnly={!isAdminCommentEditable}
                        onChange={(e) =>
                            onAdminCommentChange?.(
                                fieldKey,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="terms-form-comment-input"
                        aria-label={`Admin comment for ${fieldKey}`}
                    />
                )}
            </td>
        );
    };

    const showSellerEmployeeCol =
        employeeConsiderationPayer === EmployeeConsiderationPayer.PaidBySeller ||
        employeeConsiderationPayer === EmployeeConsiderationPayer.PaidByBoth;
    const showBuyerEmployeeCol =
        employeeConsiderationPayer === EmployeeConsiderationPayer.PaidByBuyer ||
        employeeConsiderationPayer === EmployeeConsiderationPayer.PaidByBoth;
    const showFairValueCols = isFsb === true || isSfas === true;

    const cash = Number(summary?.cashAtCloser?.cashAtClose) || 0;
    const wchValue =
        Number(summary?.cashAtCloser?.workingCapitalHoldback?.value) || 0;
    const derivedNetCash =
        summary?.cashAtCloser?.cashAtClose != null ? cash - wchValue : null;
    const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
    const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
    const dfpTotal = dfpRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const earnoutRow = dvpRows.find((r) => r.type === DeferredVariablePaymentType.Earnout);
    const syntheticRow = dvpRows.find((r) => r.type === DeferredVariablePaymentType.Synthetic);
    const earnoutRows = dvpRows.filter((r) => r.type === DeferredVariablePaymentType.Earnout);
    const syntheticRows = dvpRows.filter((r) => r.type === DeferredVariablePaymentType.Synthetic);
    const earnoutValueTotal = earnoutRows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
    const syntheticValueTotal = syntheticRows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
    const jvVal = Number(summary?.jvEquity?.row?.valueAtClosing) || 0;
    const corpRows = summary?.corporateEquity?.rows ?? [];
    const corpPreferredRow = corpRows.find((r) => r.type === CorporateEquityType.Preferred);
    const corpCommonRow = corpRows.find((r) => r.type === CorporateEquityType.Common);
    const corpVal = corpRows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
    const other = Number(summary?.other) || 0;

    const dvpFairValue = dvpRows.reduce((s, r) => s + (Number(r.fairValue) || 0), 0);
    const corpFairValue = corpRows.reduce((s, r) => s + (Number(r.fairValue) || 0), 0);
    const totalFairValue = showFairValueCols
        ? (Number(summary?.cashAtCloser?.fairValue) || 0) +
          (Number(dfpRows[0]?.fairValue) || 0) +
          dvpFairValue +
          (Number(summary?.jvEquity?.row?.fairValue) || 0) +
          corpFairValue +
          (Number(summary?.otherFairValue) || 0)
        : 0;

    const latestRef = useRef({
        onSummaryChange,
        cashAtCloser: summary?.cashAtCloser,
    });
    useEffect(() => {
        latestRef.current = {
            onSummaryChange,
            cashAtCloser: summary?.cashAtCloser,
        };
    });

    useEffect(() => {
        const { onSummaryChange: fn, cashAtCloser } = latestRef.current;
        if (derivedNetCash !== (cashAtCloser?.netCashAtCloser ?? null)) {
            fn({
                cashAtCloser: {
                    ...cashAtCloser,
                    netCashAtCloser: derivedNetCash,
                },
            });
        }
    }, [derivedNetCash]);

    const rowProps = { showSellerEmployeeCol, showBuyerEmployeeCol, showFairValueCols, showAdminComment, showComments, readOnly, errors };

    const patchDfpFirstRow = (patch: Partial<DeferredFixedPaymentRow>) =>
        onSummaryChange({
            deferredFixedPayments: {
                rows: dfpRows.length > 0
                    ? dfpRows.map((r, i) => i === 0 ? { ...r, ...patch } : r)
                    : [{ ...emptyDfpRow(null), ...patch }],
            },
        });

    const patchDvpTypedRow = (
        type: DeferredVariablePaymentType,
        patch: Partial<DeferredVariablePaymentRow>,
    ) => {
        const idx = dvpRows.findIndex((r) => r.type === type);
        onSummaryChange({
            deferredVariablePayments: {
                rows: idx >= 0
                    ? dvpRows.map((r, i) => (i === idx ? { ...r, ...patch } : r))
                    : [...dvpRows, { ...emptyDvpRow(null, type), ...patch }],
            },
        });
    };

    const patchCorpTypedRow = (
        type: CorporateEquityType,
        patch: Partial<CorporateEquityRow>,
    ) => {
        const idx = corpRows.findIndex((r) => r.type === type);
        onSummaryChange({
            corporateEquity: {
                rows: idx >= 0
                    ? corpRows.map((r, i) => (i === idx ? { ...r, ...patch } : r))
                    : [...corpRows, { ...emptyCorpRow(null, type), ...patch }],
            },
        });
    };

    const wch = summary?.cashAtCloser?.workingCapitalHoldback;
    const wchHasValue = wchValue > 0;
    const wchLabel = (
        <div className="capital-holdback-line">
            <span>Working Capital Holdback</span>
            {wchHasValue && (
                <>
                    <Input
                        type="number"
                        value={wch?.monthsHold ?? ''}
                        disabled={readOnly}
                        onChange={(e) =>
                            onSummaryChange({
                                cashAtCloser: {
                                    ...summary?.cashAtCloser,
                                    workingCapitalHoldback: {
                                        ...wch,
                                        monthsHold: e.target.value === '' ? null : Number(e.target.value),
                                    },
                                },
                            })
                        }
                        invalid={errors?.has('workingCapitalHoldbackMonths')}
                        className="form-control form-control-sm terms-form-inline-months"
                        aria-label="Months Hold"
                    />
                    <span>Months Hold</span>
                </>
            )}
        </div>
    );

    return (
        <div className="terms-form-section-underlined terms-form-price-summary-block">
            <div className="terms-form-price-summary-table-container">
                <table className="terms-form-price-summary-table">
                    <thead>
                        <tr>
                            <th style={{width: '205px', minWidth:'205px'}} >
                                Purchase Price Consideration Summary, Face Value
                                at Closing
                            </th>
                            <th className="currency-column">Face Values</th>
                            <th className="percent-column">% of Total</th>
                            {showSellerEmployeeCol && (
                                <th className="currency-column">
                                    {showBuyerEmployeeCol ? 'Consideration for Employees – Seller' : 'Consideration for Employees'}
                                </th>
                            )}
                            {showBuyerEmployeeCol && (
                                <th className="currency-column">Consideration for Employees – Buyer</th>
                            )}
                            {showFairValueCols && (
                                <>
                                    <th className="currency-column">Fair Values</th>
                                    <th className="terms-form-col-explanation">Explanation</th>
                                </>
                            )}
                            {showAdminComment && (
                                <th className="terms-form-col-comment terms-form-col-admin-comment">Admin Comment</th>
                            )}
                            {showComments && (
                                <th className="terms-form-col-comment">Buyer Comment</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Cash at Close"
                            faceValue={summary?.cashAtCloser?.cashAtClose}
                            onFaceValueChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, cashAtClose: v } })}
                            faceValueAriaLabel="Cash at Close"
                            faceValueErrorKey="cashAtClose"
                            percent={formatPercent(cash, total)}
                            considerationForEmployees={summary?.cashAtCloser?.considerationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, considerationForEmployees: v } })}
                            considerationAriaLabel="Cash at Close - Consideration for Employees"
                            considerationErrorKey="cashAtCloserConsiderationForEmployees"
                            buyerConsiderationForEmployees={summary?.cashAtCloser?.buyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, buyerConsiderationForEmployees: v } })}
                            buyerConsiderationAriaLabel="Cash at Close - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="cashAtCloserBuyerConsiderationForEmployees"
                            fairValue={summary?.cashAtCloser?.fairValue}
                            onFairValueChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, fairValue: v } })}
                            fairValueAriaLabel="Cash at Close - Fair Value"
                            fairValueErrorKey="cashAtCloserFairValue"
                            explanation={summary?.cashAtCloser?.fairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, fairValueExplanation: v } })}
                            explanationAriaLabel="Cash at Close - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.cashAtClose)}
                            commentNode={commentInput(LoiTermFormFieldKeys.cashAtClose)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label={wchLabel}
                            indented
                            faceValue={wch?.value}
                            onFaceValueChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, value: v } } })}
                            faceValueAriaLabel="Working Capital Holdback"
                            faceValueErrorKey="workingCapitalHoldbackValue"
                            percent={null}
                            considerationForEmployees={wch?.considerationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, considerationForEmployees: v } } })}
                            considerationAriaLabel="Working Capital Holdback - Consideration for Employees"
                            considerationErrorKey="workingCapitalHoldbackConsiderationForEmployees"
                            buyerConsiderationForEmployees={wch?.buyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, buyerConsiderationForEmployees: v } } })}
                            buyerConsiderationAriaLabel="Working Capital Holdback - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="workingCapitalHoldbackBuyerConsiderationForEmployees"
                            fairValue={wch?.fairValue}
                            onFairValueChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, fairValue: v } } })}
                            fairValueAriaLabel="Working Capital Holdback - Fair Value"
                            fairValueErrorKey="workingCapitalHoldbackFairValue"
                            explanation={wch?.fairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, fairValueExplanation: v } } })}
                            explanationAriaLabel="Working Capital Holdback - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.workingCapitalHoldback)}
                            commentNode={commentInput(LoiTermFormFieldKeys.workingCapitalHoldback)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Net Cash at Close"
                            faceValue={derivedNetCash}
                            allowNegativeFaceValue
                            faceValueAriaLabel="Cash at Close – Net Cash at Close"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.netCashAtClose)}
                            commentNode={commentInput(LoiTermFormFieldKeys.netCashAtClose)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Deferred Fixed Payments"
                            faceValue={dfpRows.length > 0 ? dfpTotal : null}
                            onFaceValueChange={dfpRows.length <= 1 ? (v) => onSummaryChange({ deferredFixedPayments: { rows: dfpRows.length > 0 ? dfpRows.map((r, i) => i === 0 ? { ...r, amount: v } : r) : [emptyDfpRow(v)] } }) : undefined}
                            faceValueAriaLabel="Deferred Fixed Payments"
                            faceValueErrorKey="deferredFixedPayments"
                            percent={formatPercent(dfpTotal, total)}
                            considerationForEmployees={dfpRows[0]?.considerationForEmployees}
                            onConsiderationChange={(v) => patchDfpFirstRow({ considerationForEmployees: v })}
                            considerationAriaLabel="Deferred Fixed Payments - Consideration for Employees"
                            considerationErrorKey="deferredFixedPaymentsConsiderationForEmployees"
                            buyerConsiderationForEmployees={dfpRows[0]?.buyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => patchDfpFirstRow({ buyerConsiderationForEmployees: v })}
                            buyerConsiderationAriaLabel="Deferred Fixed Payments - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="deferredFixedPaymentsBuyerConsiderationForEmployees"
                            fairValue={dfpRows[0]?.fairValue}
                            onFairValueChange={(v) => patchDfpFirstRow({ fairValue: v })}
                            fairValueAriaLabel="Deferred Fixed Payments - Fair Value"
                            fairValueErrorKey="deferredFixedPaymentsFairValue"
                            explanation={dfpRows[0]?.fairValueExplanation}
                            onExplanationChange={(v) => patchDfpFirstRow({ fairValueExplanation: v })}
                            explanationAriaLabel="Deferred Fixed Payments - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.deferredFixedPaymentsTotal)}
                            commentNode={commentInput(LoiTermFormFieldKeys.deferredFixedPaymentsTotal)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            isHeaderOnly
                            label="Deferred Variable Payments"
                            faceValueAriaLabel="Deferred Variable Payments"
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Earnout"
                            indented
                            faceValue={earnoutRows.length > 0 ? earnoutValueTotal : null}
                            onFaceValueChange={earnoutRows.length <= 1 ? (v) => patchDvpTypedRow(DeferredVariablePaymentType.Earnout, { valueAtClosing: v }) : undefined}
                            faceValueAriaLabel="Earnout"
                            faceValueErrorKey="earnoutValueAtClosing"
                            percent={formatPercent(earnoutValueTotal, total)}
                            considerationForEmployees={earnoutRow?.considerationForEmployees}
                            onConsiderationChange={(v) => patchDvpTypedRow(DeferredVariablePaymentType.Earnout, { considerationForEmployees: v })}
                            considerationAriaLabel="Earnout - Consideration for Employees"
                            considerationErrorKey="earnoutConsiderationForEmployees"
                            buyerConsiderationForEmployees={earnoutRow?.buyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => patchDvpTypedRow(DeferredVariablePaymentType.Earnout, { buyerConsiderationForEmployees: v })}
                            buyerConsiderationAriaLabel="Earnout - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="earnoutBuyerConsiderationForEmployees"
                            fairValue={earnoutRow?.fairValue}
                            onFairValueChange={(v) => patchDvpTypedRow(DeferredVariablePaymentType.Earnout, { fairValue: v })}
                            fairValueAriaLabel="Earnout - Fair Value"
                            fairValueErrorKey="earnoutFairValue"
                            explanation={earnoutRow?.fairValueExplanation}
                            onExplanationChange={(v) => patchDvpTypedRow(DeferredVariablePaymentType.Earnout, { fairValueExplanation: v })}
                            explanationAriaLabel="Earnout - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.earnout)}
                            commentNode={commentInput(LoiTermFormFieldKeys.earnout)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Synthetic JV Equity"
                            indented
                            faceValue={syntheticRows.length > 0 ? syntheticValueTotal : null}
                            onFaceValueChange={syntheticRows.length <= 1 ? (v) => patchDvpTypedRow(DeferredVariablePaymentType.Synthetic, { valueAtClosing: v }) : undefined}
                            faceValueAriaLabel="Synthetic JV Equity"
                            faceValueErrorKey="syntheticJvEquityValueAtClosing"
                            percent={formatPercent(syntheticValueTotal, total)}
                            considerationForEmployees={syntheticRow?.considerationForEmployees}
                            onConsiderationChange={(v) => patchDvpTypedRow(DeferredVariablePaymentType.Synthetic, { considerationForEmployees: v })}
                            considerationAriaLabel="Synthetic JV Equity - Consideration for Employees"
                            considerationErrorKey="syntheticJvEquityConsiderationForEmployees"
                            buyerConsiderationForEmployees={syntheticRow?.buyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => patchDvpTypedRow(DeferredVariablePaymentType.Synthetic, { buyerConsiderationForEmployees: v })}
                            buyerConsiderationAriaLabel="Synthetic JV Equity - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="syntheticJvEquityBuyerConsiderationForEmployees"
                            fairValue={syntheticRow?.fairValue}
                            onFairValueChange={(v) => patchDvpTypedRow(DeferredVariablePaymentType.Synthetic, { fairValue: v })}
                            fairValueAriaLabel="Synthetic JV Equity - Fair Value"
                            fairValueErrorKey="syntheticJvEquityFairValue"
                            explanation={syntheticRow?.fairValueExplanation}
                            onExplanationChange={(v) => patchDvpTypedRow(DeferredVariablePaymentType.Synthetic, { fairValueExplanation: v })}
                            explanationAriaLabel="Synthetic JV Equity - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.syntheticJvEquity)}
                            commentNode={commentInput(LoiTermFormFieldKeys.syntheticJvEquity)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="JV Equity"
                            faceValue={summary?.jvEquity?.row?.valueAtClosing}
                            onFaceValueChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, valueAtClosing: v } } })}
                            faceValueAriaLabel="JV Equity"
                            faceValueErrorKey="jvEquityValueAtClosing"
                            percent={formatPercent(jvVal, total)}
                            considerationForEmployees={summary?.jvEquity?.row?.considerationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, considerationForEmployees: v } } })}
                            considerationAriaLabel="JV Equity - Consideration for Employees"
                            considerationErrorKey="jvEquityConsiderationForEmployees"
                            buyerConsiderationForEmployees={summary?.jvEquity?.row?.buyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, buyerConsiderationForEmployees: v } } })}
                            buyerConsiderationAriaLabel="JV Equity - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="jvEquityBuyerConsiderationForEmployees"
                            fairValue={summary?.jvEquity?.row?.fairValue}
                            onFairValueChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, fairValue: v } } })}
                            fairValueAriaLabel="JV Equity - Fair Value"
                            fairValueErrorKey="jvEquityFairValue"
                            explanation={summary?.jvEquity?.row?.fairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, fairValueExplanation: v } } })}
                            explanationAriaLabel="JV Equity - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.jvEquityTotal)}
                            commentNode={commentInput(LoiTermFormFieldKeys.jvEquityTotal)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            isHeaderOnly
                            label="Corporate Equity"
                            faceValueAriaLabel="Corporate Equity"
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Common Corp Equity"
                            indented
                            faceValue={corpCommonRow?.valueAtClosing ?? null}
                            onFaceValueChange={(v) => patchCorpTypedRow(CorporateEquityType.Common, { valueAtClosing: v })}
                            faceValueAriaLabel="Common Corp Equity"
                            faceValueErrorKey="corporateEquityCommonValueAtClosing"
                            percent={formatPercent(Number(corpCommonRow?.valueAtClosing) || 0, total)}
                            considerationForEmployees={corpCommonRow?.considerationForEmployees}
                            onConsiderationChange={(v) => patchCorpTypedRow(CorporateEquityType.Common, { considerationForEmployees: v })}
                            considerationAriaLabel="Common Corp Equity - Consideration for Employees"
                            considerationErrorKey="corporateEquityCommonConsiderationForEmployees"
                            buyerConsiderationForEmployees={corpCommonRow?.buyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => patchCorpTypedRow(CorporateEquityType.Common, { buyerConsiderationForEmployees: v })}
                            buyerConsiderationAriaLabel="Common Corp Equity - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="corporateEquityCommonBuyerConsiderationForEmployees"
                            fairValue={corpCommonRow?.fairValue}
                            onFairValueChange={(v) => patchCorpTypedRow(CorporateEquityType.Common, { fairValue: v })}
                            fairValueAriaLabel="Common Corp Equity - Fair Value"
                            fairValueErrorKey="corporateEquityCommonFairValue"
                            explanation={corpCommonRow?.fairValueExplanation}
                            onExplanationChange={(v) => patchCorpTypedRow(CorporateEquityType.Common, { fairValueExplanation: v })}
                            explanationAriaLabel="Common Corp Equity - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.corporateEquityCommon)}
                            commentNode={commentInput(LoiTermFormFieldKeys.corporateEquityCommon)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Preferred Corp Equity"
                            indented
                            faceValue={corpPreferredRow?.valueAtClosing ?? null}
                            onFaceValueChange={(v) => patchCorpTypedRow(CorporateEquityType.Preferred, { valueAtClosing: v })}
                            faceValueAriaLabel="Preferred Corp Equity"
                            faceValueErrorKey="corporateEquityPreferredValueAtClosing"
                            percent={formatPercent(Number(corpPreferredRow?.valueAtClosing) || 0, total)}
                            considerationForEmployees={corpPreferredRow?.considerationForEmployees}
                            onConsiderationChange={(v) => patchCorpTypedRow(CorporateEquityType.Preferred, { considerationForEmployees: v })}
                            considerationAriaLabel="Preferred Corp Equity - Consideration for Employees"
                            considerationErrorKey="corporateEquityPreferredConsiderationForEmployees"
                            buyerConsiderationForEmployees={corpPreferredRow?.buyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => patchCorpTypedRow(CorporateEquityType.Preferred, { buyerConsiderationForEmployees: v })}
                            buyerConsiderationAriaLabel="Preferred Corp Equity - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="corporateEquityPreferredBuyerConsiderationForEmployees"
                            fairValue={corpPreferredRow?.fairValue}
                            onFairValueChange={(v) => patchCorpTypedRow(CorporateEquityType.Preferred, { fairValue: v })}
                            fairValueAriaLabel="Preferred Corp Equity - Fair Value"
                            fairValueErrorKey="corporateEquityPreferredFairValue"
                            explanation={corpPreferredRow?.fairValueExplanation}
                            onExplanationChange={(v) => patchCorpTypedRow(CorporateEquityType.Preferred, { fairValueExplanation: v })}
                            explanationAriaLabel="Preferred Corp Equity - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.corporateEquityPreferred)}
                            commentNode={commentInput(LoiTermFormFieldKeys.corporateEquityPreferred)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Other"
                            faceValue={summary?.other}
                            onFaceValueChange={(v) => onSummaryChange({ other: v })}
                            faceValueAriaLabel="Other"
                            faceValueErrorKey="other"
                            percent={formatPercent(other, total)}
                            considerationForEmployees={summary?.otherConsiderationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ otherConsiderationForEmployees: v })}
                            considerationAriaLabel="Other - Consideration for Employees"
                            considerationErrorKey="otherConsiderationForEmployees"
                            buyerConsiderationForEmployees={summary?.otherBuyerConsiderationForEmployees}
                            onBuyerConsiderationChange={(v) => onSummaryChange({ otherBuyerConsiderationForEmployees: v })}
                            buyerConsiderationAriaLabel="Other - Consideration for Employees - Buyer"
                            buyerConsiderationErrorKey="otherBuyerConsiderationForEmployees"
                            fairValue={summary?.otherFairValue}
                            onFairValueChange={(v) => onSummaryChange({ otherFairValue: v })}
                            fairValueAriaLabel="Other - Fair Value"
                            fairValueErrorKey="otherFairValue"
                            explanation={summary?.otherFairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ otherFairValueExplanation: v })}
                            explanationAriaLabel="Other - Fair Value Explanation"
                            adminCommentNode={adminCommentInput(LoiTermFormFieldKeys.other)}
                            commentNode={commentInput(LoiTermFormFieldKeys.other)}
                        />

                        {total > 0 && (
                            <tr className="terms-form-summary-total">
                                <td className="terms-form-col-item table-label-cell">
                                    Total Face Value, at Closing
                                </td>
                                <td className="terms-form-col-value">
                                    <Input
                                        type="text"
                                        value={Helpers.currencyFormat(total, false)}
                                        disabled
                                        readOnly
                                        className="form-control loi-form-input"
                                        aria-label="Total Face Value at Closing"
                                    />
                                </td>
                                <td className="terms-form-col-percent">
                                    <Input
                                        type="text"
                                        value="100%"
                                        disabled
                                        readOnly
                                        className="form-control loi-form-input terms-form-percent-label"
                                        aria-label="Percent of Total"
                                    />
                                </td>
                                {showSellerEmployeeCol && <td className="terms-form-col-value" />}
                                {showBuyerEmployeeCol && <td className="terms-form-col-value" />}
                                {showFairValueCols && (
                                    <>
                                        <td className="terms-form-col-value">
                                            <CurrencyInput
                                                name="totalFairValue"
                                                value={totalFairValue > 0 ? String(totalFairValue) : ''}
                                                allowDecimals
                                                prefix="$"
                                                disabled
                                                className="form-control loi-form-input"
                                                aria-label="Total Fair Value"
                                            />
                                        </td>
                                        <td className="terms-form-col-explanation" />
                                    </>
                                )}
                                {adminCommentCell(LoiTermFormFieldKeys.totalPurchasePrice)}
                                {commentCell(LoiTermFormFieldKeys.totalPurchasePrice)}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
